import React, { useCallback, useEffect } from 'react';
import '../../../images/map-pin-fill.svg';
import '../../../images/circle-fill.svg';
import { Retailer } from '../../Types/Retailer';
import { CUSTOM_MAP_STYLE } from './MapStyling';
import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClusterer,
  useJsApiLoader,
} from '@react-google-maps/api';
import MarkerInfoBox from './MarkerInfoBox';

interface RetailerListMapProps {
  retailerList: Retailer[];
  activeRetailer: Retailer | null;
  setActiveRetailer: (retailer: Retailer | null) => void;
  isLoading: boolean;
  googleApiKey: string;
}

const RetailerListMap = ({
  retailerList,
  activeRetailer,
  setActiveRetailer,
  googleApiKey,
  isLoading: isLoadingRetailers,
}: RetailerListMapProps) => {
  const [map, setMap] = React.useState<google.maps.Map | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleApiKey,
  });

  const handleOnLoad = useCallback((map) => setMap(map), []);

  const onUnmount = React.useCallback(function callback() {
    setMap(null);
  }, []);
  const handleActiveMarkerClick = (retailer: Retailer) => {
    if (activeRetailer === retailer) {
      setActiveRetailer(null);
      return;
    }
    setActiveRetailer(retailer);
  };
  useEffect(() => {
    if (!map) return;
    const bounds = new window.google.maps.LatLngBounds();
    retailerList.map((retailer) => {
      bounds.extend(retailer.position);
    });
    map.fitBounds(bounds);
  }, [map, retailerList]);

  const MAP_OPTIONS = {
    styles: CUSTOM_MAP_STYLE,
    streetViewControl: true,
    maxZoom: 18,
  };

  return isLoaded && !isLoadingRetailers ? (
    <GoogleMap
      mapContainerClassName="aspect-square lg:aspect-video w-full rounded max-h-[620px]"
      options={MAP_OPTIONS}
      onLoad={handleOnLoad}
      onUnmount={onUnmount}
    >
      <MarkerClusterer
        averageCenter
        minimumClusterSize={2}
        styles={[
          {
            url: '/ui/images/circle-fill.svg',
            height: 30,
            textLineHeight: 30,
            width: 30,
            textColor: 'white',
          },
          {
            url: '/ui/images/circle-fill.svg',
            height: 35,
            textLineHeight: 35,
            width: 35,
            textColor: 'white',
          },
          {
            url: '/ui/images/circle-fill.svg',
            height: 40,
            textLineHeight: 40,
            width: 40,
            textColor: 'white',
          },
          {
            url: '/ui/images/circle-fill.svg',
            height: 45,
            textLineHeight: 45,
            width: 45,
            textColor: 'white',
          },
          {
            url: '/ui/images/circle-fill.svg',
            height: 50,
            textLineHeight: 50,
            width: 50,
            textColor: 'white',
          },
        ]}
      >
        {(clusterer) =>
          retailerList &&
          retailerList.length > 0 &&
          retailerList.map((retailer: Retailer) => (
            <Marker
              key={`${retailer.name}-${retailer.position.lat},${retailer.position.lng}`}
              position={retailer.position}
              onClick={() => handleActiveMarkerClick(retailer)}
              clusterer={clusterer}
              icon={{
                url: '/ui/images/map-pin-fill.svg',
                scaledSize: new google.maps.Size(30, 30),
              }}
            >
              {activeRetailer === retailer ? (
                <InfoWindow onCloseClick={() => setActiveRetailer(null)}>
                  <MarkerInfoBox retailer={retailer} />
                </InfoWindow>
              ) : null}
            </Marker>
          ))
        }
      </MarkerClusterer>
    </GoogleMap>
  ) : (
    <div className="skeleton max-h-[620px] w-full rounded bg-gray-200 aspect-square lg:aspect-video"></div>
  );
};
export default React.memo(RetailerListMap);
